import { useMutation } from "@apollo/client";
import { useCallback } from "react";
import { UPDATE_PROFILE_MUTATION } from "@/api/mutations/updateProfileMutation";
import {
  UpdateProfileMutation,
  UpdateProfileMutationVariables,
} from "@/__generated__/types";
import { MutationErrorHandler } from "@/util/mutationUtils";

export interface UseUpdateProfileParams {
  onCompleted?: (data: UpdateProfileMutation) => void;
  onError?: (error: unknown) => void;
}

export interface UpdateProfileResult {
  success: boolean;
  errors: {
    message: string;
    title: string;
  }[];
}

export interface UseUpdateProfileReturn {
  loading: boolean;
  error: unknown;
}

export interface UpdateProfileInput {
  userId: string;
  firstName: string;
  lastName: string;
  bio?: string | null;
  locationCityId?: string | null;
  interestIds?: string[] | null;
  majorIds?: string[] | null;
  profilePhoto?: UpdateProfileMutationVariables["profilePhoto"];
}

export const useUpdateProfile = ({
  onCompleted,
  onError,
}: UseUpdateProfileParams = {}): [
  (input: UpdateProfileInput) => Promise<UpdateProfileResult>,
  UseUpdateProfileReturn,
] => {
  const [updateProfileMutation, { loading, error }] = useMutation<
    UpdateProfileMutation,
    UpdateProfileMutationVariables
  >(UPDATE_PROFILE_MUTATION, {
    onCompleted,
    onError,
    refetchQueries: ["CurrentUser"],
    awaitRefetchQueries: true,
  });

  const updateProfile = useCallback(
    async (input: UpdateProfileInput): Promise<UpdateProfileResult> => {
      try {
        const result = await updateProfileMutation({
          variables: {
            userId: input.userId,
            firstName: input.firstName,
            lastName: input.lastName,
            bio: input.bio,
            locationCityId: input.locationCityId,
            interestIds: input.interestIds || [],
            majorIds: input.majorIds || [],
            profilePhoto: input.profilePhoto,
          },
        });

        const errorHandler = MutationErrorHandler.fromResult(
          result,
          result.errors,
        );
        return errorHandler.toResult();
      } catch (error) {
        return {
          success: false,
          errors: [
            {
              message: error instanceof Error ? error.message : "Unknown error",
              title: "Mutation Error",
            },
          ],
        };
      }
    },
    [updateProfileMutation],
  );

  return [updateProfile, { loading, error }];
};
